import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { BUSINESS_TOOL_SECTIONS, BUSINESS_TOOL_SUB_SECTIONS } from 'constants/businessTool';
import { t } from 'i18next';

import { concatWithDot } from 'helpers/string';
import { ObjectValues } from 'helpers/types';

import { Assets, LiabilitiesAndEquity } from '../Sections';
import { assetsInitialValues, IAssetsFormValuesType } from '../Sections/Assets/utils/form';
import {
  ILiabilitiesFormValuesType,
  liabilitiesAndEquityInitialValues,
} from '../Sections/LiabilitiesAndEquity/utils/form';

export const FIRST_COLUMN_WIDTH = 210;

export const prefix =
  `${BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN}.${BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_BALANCE_SHEETS}` as const;

export const requestsPrefix =
  `${BUSINESS_TOOL_SECTIONS.FINANCIAL_PLAN}.${BUSINESS_TOOL_SUB_SECTIONS.FINANCIAL_PLAN.PROJECTED_CASH_FLOW}` as const;

export const PROJECTED_BALANCE_SHEETS_TABS_NAMES = {
  ASSETS: 'initialAssets',
  LIABILITIES_AND_EQUITY: 'initialLiabilities',
} as const;

export const useGetProjectedBalanceSheetsTabs = () => {
  const { t, i18n } = useTranslation();
  return useMemo(() => {
    const PROJECTED_BALANCE_SHEETS_TABS = [
      {
        label: t('financialPlan.projectedBalanceSheets.assets.title'),
        value: PROJECTED_BALANCE_SHEETS_TABS_NAMES.ASSETS,
      },
      {
        label: t('financialPlan.projectedBalanceSheets.liabilitiesAndEquity.title'),
        value: PROJECTED_BALANCE_SHEETS_TABS_NAMES.LIABILITIES_AND_EQUITY,
      },
    ];
    return PROJECTED_BALANCE_SHEETS_TABS;
  }, [t, i18n]);
};

export type IProjectedBalanceSheetsValuesType = ObjectValues<typeof PROJECTED_BALANCE_SHEETS_TABS_NAMES>;

export const projectedBalanceSheetsOptionsToComponentMap: Record<IProjectedBalanceSheetsValuesType, FC> = {
  [PROJECTED_BALANCE_SHEETS_TABS_NAMES.ASSETS]: Assets,
  [PROJECTED_BALANCE_SHEETS_TABS_NAMES.LIABILITIES_AND_EQUITY]: LiabilitiesAndEquity,
};

export const PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES = {
  CURRENCY: 'currency',
  ASSETS: 'assets',
  LIABILITIES_AND_EQUITY: 'liabilitiesAndEquity',
  COMPANY_MAIN_COMPONENTS: 'companyMainComponents',
  SHORT_TERM_LIABILITIES_FINANCE: 'shortTermLiabilitiesFinance',
  NON_CURRENT_ASSETS_CHANGE: 'nonCurrentAssetsChange',
  STRUCTURE_OF_NON_CURRENT_ASSETS: 'structureOfNonCurrentAssets',
  SHORT_TERM_LIABILITIES_MAIN_COMPONENTS: 'shortTermLiabilitiesMainComponents',
  SHORT_TERM_FINANCING_SOURCES: 'shortTermFinancingSources',
  LONG_TERM_LIABILITIES_MAIN_COMPONENTS: 'longTermLiabilitiesMainComponents',
  COMPANY_EQUITY_STRUCTURE: 'companyEquityStructure',
} as const;

export const assetsPrefix = concatWithDot(prefix, PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.ASSETS);
export const liabilitiesAndEquityPrefix = concatWithDot(
  prefix,
  PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.LIABILITIES_AND_EQUITY,
);

export const INPUT_FIELDS_LIST: Record<'title' | 'name', string>[] = [
  {
    title: t('financialPlan.projectedBalanceSheets.companyMainComponents'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.COMPANY_MAIN_COMPONENTS,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.shortTermLiabilitiesFinance'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_LIABILITIES_FINANCE,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.nonCurrentAssetsChange'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.NON_CURRENT_ASSETS_CHANGE,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.structureOfNonCurrentAssets'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.STRUCTURE_OF_NON_CURRENT_ASSETS,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.shortTermLiabilitiesMainComponents'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_LIABILITIES_MAIN_COMPONENTS,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.shortTermFinancingSources'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_FINANCING_SOURCES,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.longTermLiabilitiesMainComponents'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.LONG_TERM_LIABILITIES_MAIN_COMPONENTS,
  },
  {
    title: t('financialPlan.projectedBalanceSheets.companyEquityStructure'),
    name: PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.COMPANY_EQUITY_STRUCTURE,
  },
];

export interface IProjectedBalanceSheets {
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.ASSETS]: IAssetsFormValuesType;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.LIABILITIES_AND_EQUITY]: ILiabilitiesFormValuesType;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.COMPANY_MAIN_COMPONENTS]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_LIABILITIES_FINANCE]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.NON_CURRENT_ASSETS_CHANGE]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.STRUCTURE_OF_NON_CURRENT_ASSETS]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_LIABILITIES_MAIN_COMPONENTS]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_FINANCING_SOURCES]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.LONG_TERM_LIABILITIES_MAIN_COMPONENTS]: string;
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.COMPANY_EQUITY_STRUCTURE]: string;
}

export const projectedBalanceSheetsInitialValues: IProjectedBalanceSheets = {
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.ASSETS]: assetsInitialValues,
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.LIABILITIES_AND_EQUITY]: liabilitiesAndEquityInitialValues,
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.COMPANY_MAIN_COMPONENTS]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_LIABILITIES_FINANCE]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.NON_CURRENT_ASSETS_CHANGE]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.STRUCTURE_OF_NON_CURRENT_ASSETS]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_LIABILITIES_MAIN_COMPONENTS]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.SHORT_TERM_FINANCING_SOURCES]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.LONG_TERM_LIABILITIES_MAIN_COMPONENTS]: '',
  [PROJECTED_BALANCE_SHEETS_FORM_SECTION_NAMES.COMPANY_EQUITY_STRUCTURE]: '',
};
